import * as Sentry from '@sentry/react';

import adminVersion from '../../../package.json';
import { sentryConfig } from '../../config/sentryKey';
import { createBrowserHistory } from 'history';
import { sentryBeforeSend } from './sentryIgnoreConfig';
// Lazy import moved to identifyUserFromStore to avoid circular dependencies in tests

const getEnvironment = (nodeEnv: string | undefined): string => {
	const envMapping: { [key: string]: string } = {
		development: 'development',
		production: 'production',
		test: 'test',
	};
	return envMapping[nodeEnv || ''] || 'unknown';
};

const sentrySetup = () => {
	const envSentry = import.meta.env.REACT_APP_SENTRY_ENVIRONMENT || getEnvironment(process.env.NODE_ENV);
	const history = createBrowserHistory();
	Sentry.init({
		dsn: sentryConfig.dsn,
		environment: envSentry,
		release: import.meta.env.REACT_APP_VERSION || adminVersion?.version || 'no-version',
		integrations: [
			new Sentry.BrowserTracing({
				routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
			}),
		],
		tracesSampleRate: 1.0,
		maxBreadcrumbs: envSentry === 'production' || envSentry === 'beta' ? 50 : 100,
		debug: envSentry === 'development',
		attachStacktrace: true,
		autoSessionTracking: true,
		normalizeDepth: 3,
		beforeSend: sentryBeforeSend,
	});
};

/**
 * Set user identity for Sentry tracking
 * Call this after successful authentication
 */
const identifyUser = (userData?: {
	email?: string;
	id?: string;
	displayName?: string;
	organizationId?: string;
	role?: string;
}) => {
	if (userData) {
		Sentry.setUser({
			email: userData.email,
			id: userData.id,
			username: userData.displayName,
		});

		Sentry.setContext('User Details', {
			email: userData.email || '-',
			displayName: userData.displayName || '-',
			organizationId: userData.organizationId || '-',
			role: userData.role || '-',
		});
	}
};

/**
 * Identify user from Redux store state
 * Useful for ensuring user context is set before logging
 */
const identifyUserFromStore = () => {
	try {
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const { store } = require('store/configStore');
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const { selectProfile } = require('store/selectors/profile');
		const state = store.getState();
		const { auth } = state.firebase;
		const profile = selectProfile(state);

		if (auth?.uid) {
			Sentry.setUser({
				email: auth.email || undefined,
				id: auth.uid,
				username: profile?.displayName,
			});

			Sentry.setContext('User Details', {
				email: auth?.email || '-',
				displayName: profile?.displayName || '-',
				organizationId: profile?.organization || '-',
				role: profile?.role || '-',
				status: profile?.status || '-',
				language: profile?.language || '-',
			});
		}
	} catch (e) {
		// Store may not be initialized yet, ignore
	}
};

/**
 * Clear user identity on logout
 */
const clearUser = () => {
	Sentry.setUser(null);
};

/**
 * Log an error with additional context to Sentry
 */
const logErrorWithContext = (error: unknown, context: string, metadata?: Record<string, any>) => {
	// Ensure user is identified before logging
	identifyUserFromStore();

	Sentry.addBreadcrumb({
		category: 'error',
		message: context,
		level: 'error',
		data: metadata,
	});

	if (error instanceof Error) {
		Sentry.captureException(error, {
			tags: { context },
			extra: metadata,
		});
	} else {
		Sentry.captureMessage(String(error), {
			level: 'error',
			tags: { context },
			extra: metadata,
		});
	}
};

/**
 * Track business operations (success/failure with metadata)
 */
const trackOperation = (operation: string, result: 'success' | 'failure', metadata?: Record<string, any>) => {
	Sentry.addBreadcrumb({
		category: 'operation',
		message: `${operation}: ${result}`,
		level: result === 'success' ? 'info' : 'warning',
		data: {
			operation,
			result,
			...metadata,
		},
	});
};

/**
 * Track performance for slow operations (>threshold ms, default 1000ms)
 */
const trackPerformance = (operation: string, metrics: { duration?: number; success?: boolean }, threshold = 1000) => {
	const { duration, success } = metrics;

	// Only log if duration exceeds threshold
	if (duration && duration > threshold) {
		Sentry.addBreadcrumb({
			category: 'performance',
			message: `Slow operation: ${operation}`,
			level: 'warning',
			data: {
				operation,
				duration_ms: duration,
				success,
				threshold_ms: threshold,
			},
		});
	}
};

/**
 * Wrap async operations with automatic timing and error tracking
 */
const trackAsyncOperation = async <T>(
	name: string,
	fn: () => Promise<T>,
	metadata?: Record<string, any>,
): Promise<T> => {
	const startTime = Date.now();

	try {
		const result = await fn();
		const duration = Date.now() - startTime;

		trackOperation(name, 'success', { ...metadata, duration_ms: duration });
		trackPerformance(name, { duration, success: true });

		return result;
	} catch (error) {
		const duration = Date.now() - startTime;

		trackOperation(name, 'failure', { ...metadata, duration_ms: duration });
		logErrorWithContext(error, `async_operation_${name}`, metadata);

		throw error;
	}
};

/**
 * Track user actions (forms, buttons, navigation)
 */
const trackUserAction = (action: string, details?: Record<string, any>) => {
	Sentry.addBreadcrumb({
		category: 'user_action',
		message: action,
		level: 'info',
		data: details,
	});
};

/**
 * Generate a unique session ID for tracking multi-step operations
 */
const generateSessionId = (): string => {
	return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Track the start of a multi-step session/operation
 */
const trackSessionStart = (sessionType: string, sessionId: string, metadata?: Record<string, any>) => {
	Sentry.addBreadcrumb({
		category: 'session',
		message: `${sessionType}: started`,
		level: 'info',
		data: {
			session_type: sessionType,
			session_id: sessionId,
			event: 'start',
			...metadata,
		},
	});
};

/**
 * Track the end of a multi-step session/operation
 */
const trackSessionEnd = (
	sessionType: string,
	sessionId: string,
	result: 'success' | 'failure',
	metadata?: Record<string, any>,
) => {
	Sentry.addBreadcrumb({
		category: 'session',
		message: `${sessionType}: ${result}`,
		level: result === 'success' ? 'info' : 'warning',
		data: {
			session_type: sessionType,
			session_id: sessionId,
			event: 'end',
			result,
			...metadata,
		},
	});
};

export {
	sentrySetup,
	identifyUser,
	identifyUserFromStore,
	clearUser,
	logErrorWithContext,
	trackOperation,
	trackPerformance,
	trackAsyncOperation,
	trackUserAction,
	generateSessionId,
	trackSessionStart,
	trackSessionEnd,
};
